// E12-t30 (web half) — THE DEVICE-APPROVAL PAGE.   route: /auth/device
//
// Contract: git-host/src/auth.ts — `verification_uri` is THIS deployment's own
//   `${origin}/auth/device`, printed by djc/src/commands/auth.js and opened by a
//   person. Until this file existed nothing answered there, so the human half of
//   the login was a hand-made fetch. The two routes behind this page are:
//     GET  /api/auth/device/pending?user_code=…   what is about to be authorized
//     POST /api/auth/device/approve               the moment IDENTITY IS ATTACHED
// Contract: vault/Backlog/E12 Testable Surface and Authoring.md t30 — and its
//   last sentence is the reason for the shape of the decline control below: "Also
//   unbuilt and deliberately unpinned: a DENY button, because
//   `denyDeviceAuthorization` does not exist and `access_denied` is a status
//   nothing in the repo can produce."
// Contract: vault/Flows/Tenancy and Guards.md f5 (device-code flow for the CLI)
//   and s2 (a refusal is an ordinary answer with the guard NAMED).
// Contract: vault/Apps/CLI/CLI.md — the terminal prints the code and this page is
//   where the person confirms it. The code is shown BACK to them so the two can be
//   compared: that comparison is the whole security value of the user code, and a
//   page that only says "approve?" removes it.
//
// WHAT THIS PAGE IS FOR, said plainly, because it is a page whose job is to make
// somebody stop and think: approving attaches the APPROVER's identity to a
// terminal somewhere. auth.ts is explicit — "the session the CLI collects belongs
// to whoever approved, and to nobody else" — so the copy names the client as a
// CLAIM ("a client calling itself djc"), states the scope as it was asked for, and
// says out loud that a code the reader did not just cause should not be approved.
// A phishable page is one that reassures; this one reports.
//
// THE DECLINE IS HONEST ABOUT WHAT IT CANNOT DO. There is no deny route in the
// repository, so "Don't approve" cannot revoke anything — it ends this page's part
// in the exchange and says, in the answer, that the code simply stays pending
// until it expires. Wiring that button to nothing while LOOKING like a revocation
// is the failure mode this file refuses; naming the gap is the alternative.
//
// Presentational, transport injected (src/api/auth.ts), no global fetch.
//
// E14-t6 — the card, the buttons, the field and the refusal box were hand-built
// here out of tokens; they are now Card, Button, TextInput and Banner. TWO things
// resisted the design system and both are kept ON PURPOSE, not by omission:
//   · the unverified-client warning keeps `role="note"`, because that is the role
//     device-approval.test.tsx reads it by and Banner only ever emits alert or
//     status. It is a Card in the warning tint WITH the role set, so the surface
//     is the design system's and the contract survives;
//   · `.dj-device-code` keeps one declaration — letter-spacing — because the whole
//     security value of the user code is that a person compares it character by
//     character with the one their terminal printed.
import { useEffect, useState, type FormEvent } from 'react';

import { Banner } from '@astryxdesign/core/Banner';
import { Button } from '@astryxdesign/core/Button';
import { Card } from '@astryxdesign/core/Card';
import { Heading } from '@astryxdesign/core/Heading';
import { Stack } from '@astryxdesign/core/Stack';
import { Text } from '@astryxdesign/core/Text';
import { TextInput } from '@astryxdesign/core/TextInput';

import { AuthRefusal, type AuthClient, type DevicePending } from '../api/auth';

import './device-approval.css';

export interface DeviceApprovalPageProps {
  client: AuthClient;
  /** `?user_code=` from the url. Absent when the person opened the bare
   *  `verification_uri` the CLI prints, which is the RFC 8628 §3.3 case: they
   *  type the code themselves. */
  userCode?: string | null;
  /** the signed-in person — approving signs THIS identity into the terminal */
  actorEmail: string;
}

type Phase =
  /** no code to ask about yet: the person types the one their terminal printed */
  | { kind: 'ask' }
  | { kind: 'loading' }
  | { kind: 'ready'; pending: DevicePending }
  | { kind: 'approving'; pending: DevicePending }
  | { kind: 'approved'; userCode: string }
  | { kind: 'declined'; pending: DevicePending }
  | { kind: 'refused'; message: string };

/**
 * The control plane's machine codes, turned into the sentence a person can ACT on
 * — and only where acting is possible.
 *
 * `expired_token` keeps its own name on the wire for exactly this reason
 * (auth.ts: "it is the one refusal a person can act on"), so it is the one that
 * gets a remedy. `invalid_grant` covers an unknown code, a spent one and a
 * declined one with the same bytes ON PURPOSE — the user code is ~34.6 bits and
 * the route is reachable by anyone with a session — so the sentence here must not
 * split them back apart, and it says why rather than pretending to know which.
 */
function sentenceFor(refusal: AuthRefusal): string {
  switch (refusal.code) {
    case 'expired_token':
      return 'That code has expired. Run `djc auth login` in your terminal again and open the new link.';
    case 'invalid_grant':
      return (
        'That code is not one this forge can approve. It may have been mistyped, already used, ' +
        'or already finished — the forge answers all three alike, so that nobody can use this ' +
        'page to find out which codes are real.'
      );
    case 'forbidden':
      // The evaluator's own words, with the guard it named (Tenancy s2).
      return refusal.guard === null ? refusal.message : `${refusal.message} (guard: ${refusal.guard})`;
    case 'unauthenticated':
      return 'Your session ended before the code could be read. Sign in again and reopen this link.';
    default:
      return refusal.message;
  }
}

/** Remaining life, as a person would say it. Never a countdown: a ticking clock
 *  would re-render this page every second to tell somebody something they can
 *  read once, and the server's number is already whole seconds. */
function remaining(seconds: number): string {
  if (seconds <= 0) return 'It has expired.';
  if (seconds < 60) return 'It expires in less than a minute.';
  const minutes = Math.round(seconds / 60);
  return `It expires in about ${minutes} minute${minutes === 1 ? '' : 's'}.`;
}

/**
 * E14-t6 — the two attributes the design system's TextInput does not carry, put
 * back on the element it owns.
 *
 * BaseProps OMITS `autoComplete` and `spellCheck` from every Astryx component, so
 * `<TextInput autoComplete="one-time-code">` does not type-check. Dropping them
 * would have been a silent regression rather than a style cleanup: `one-time-code`
 * is what makes a phone offer the code from the clipboard or an SMS, and this is
 * the one field in the app where that matters. TextInput forwards `ref` to the
 * real <input> (TextInputProps.ref: React.Ref<HTMLInputElement>), so the callback
 * ref sets both on the element the component already renders. Nothing is
 * re-implemented — one attribute is restored.
 */
function oneTimeCodeAttrs(input: HTMLInputElement | null): void {
  if (input === null) return;
  input.setAttribute('autocomplete', 'one-time-code');
  input.spellcheck = false;
}

export function DeviceApprovalPage({ client, userCode = null, actorEmail }: DeviceApprovalPageProps) {
  const [typed, setTyped] = useState(userCode ?? '');
  /** the code the page has actually ASKED about — null until there is one */
  const [asked, setAsked] = useState<string | null>(userCode ?? null);
  const [phase, setPhase] = useState<Phase>(userCode ? { kind: 'loading' } : { kind: 'ask' });

  useEffect(() => {
    if (asked === null) return;
    let live = true;
    setPhase({ kind: 'loading' });
    client.pendingDevice(asked).then(
      (pending) => {
        if (live) setPhase({ kind: 'ready', pending });
      },
      (err: unknown) => {
        if (!live) return;
        setPhase({
          kind: 'refused',
          message:
            err instanceof AuthRefusal
              ? sentenceFor(err)
              : 'The forge could not be reached, so nothing has been approved.',
        });
      },
    );
    return () => {
      live = false;
    };
  }, [asked, client]);

  function ask(event: FormEvent) {
    event.preventDefault();
    const code = typed.trim();
    // Sent EXACTLY as it was typed apart from surrounding space: the consonant
    // alphabet exists so a person can retype the printed code unchanged, and
    // re-casing or de-hyphenating it here would undo that (auth.ts says so).
    if (code !== '') setAsked(code);
  }

  async function approve(pending: DevicePending) {
    setPhase({ kind: 'approving', pending });
    try {
      await client.approveDevice(pending.userCode);
      setPhase({ kind: 'approved', userCode: pending.userCode });
    } catch (err: unknown) {
      setPhase({
        kind: 'refused',
        message:
          err instanceof AuthRefusal
            ? sentenceFor(err)
            : 'The forge could not be reached, so nothing has been approved.',
      });
    }
  }

  return (
    <div className="dj-device" data-page="device">
      <Heading level={1} type="display-3">
        Approve a terminal sign-in
      </Heading>
      <Body
        phase={phase}
        typed={typed}
        actorEmail={actorEmail}
        onType={setTyped}
        onAsk={ask}
        onApprove={approve}
        onDecline={(pending) => setPhase({ kind: 'declined', pending })}
        onRetype={() => {
          setAsked(null);
          setTyped('');
          setPhase({ kind: 'ask' });
        }}
      />
    </div>
  );
}

function Body({
  phase,
  typed,
  actorEmail,
  onType,
  onAsk,
  onApprove,
  onDecline,
  onRetype,
}: {
  phase: Phase;
  typed: string;
  actorEmail: string;
  onType: (value: string) => void;
  onAsk: (event: FormEvent) => void;
  onApprove: (pending: DevicePending) => void;
  onDecline: (pending: DevicePending) => void;
  onRetype: () => void;
}) {
  if (phase.kind === 'ask') {
    // The form's accessible name is NOT the field's: a landmark and a control
    // that share a name are two things a reader cannot tell apart.
    return (
      <Card padding={4} elevation="none">
        <form onSubmit={onAsk} aria-label="Enter the code from your terminal">
          <Stack direction="vertical" gap={3} hAlign="start">
            <Text as="p" color="secondary">
              Your terminal printed a one-time code when you ran <code>djc auth login</code>. Type
              it here exactly as it was printed.
            </Text>
            <TextInput
              label="One-time code"
              type="text"
              value={typed}
              onChange={onType}
              width="100%"
              ref={oneTimeCodeAttrs}
            />
            <Button type="submit" variant="primary" label="Continue" />
          </Stack>
        </form>
      </Card>
    );
  }

  if (phase.kind === 'loading') {
    return (
      <Text as="p" color="secondary" role="status">
        Looking up that code…
      </Text>
    );
  }

  if (phase.kind === 'refused') {
    return (
      <Card padding={4} elevation="none">
        <Stack direction="vertical" gap={3} hAlign="start">
          {/* status="error" is what gives this role="alert" — the contract
              device-approval.test.tsx reads the refusal by. */}
          <Banner status="error" title={phase.message} container="card" />
          <Text as="p" color="secondary">
            Nothing has been approved.
          </Text>
          <Button type="button" variant="secondary" label="Type another code" onClick={onRetype} />
        </Stack>
      </Card>
    );
  }

  if (phase.kind === 'approved') {
    return (
      <Card padding={4} elevation="none">
        <Stack direction="vertical" gap={3} hAlign="start">
          {/* status="success" renders role="status" (Banner statusRole). */}
          <Banner
            status="success"
            container="card"
            title={`Approved. Return to your terminal — it collects the session on its next poll, signed in as ${actorEmail}.`}
          />
          {/* E15-t2 — this span carried the TRACKING and not the FACE: nothing
              in the app gives `.dj-device-code` a font-family, so a user code
              mentioned inline was set in the sans face with 0.12em between its
              letters, while the same code in the panel above it was mono. The
              one code a person is asked to compare character by character was
              being shown to them two different ways. `type="code"` is the face;
              the class keeps being what it says it is, the tracking. */}
          <Text as="p" color="secondary">
            The code{' '}
            <Text type="code" color="inherit" className="dj-device-code">
              {phase.userCode}
            </Text>{' '}
            is spent: it cannot be approved a second time, and this page cannot approve it for
            anyone else.
          </Text>
        </Stack>
      </Card>
    );
  }

  if (phase.kind === 'declined') {
    return (
      <Card padding={4} elevation="none">
        <Stack direction="vertical" gap={3} hAlign="start">
          <Banner status="info" container="card" title="Nothing was approved." />
          <Text as="p" color="secondary">
            The forge has no way to cancel a code that was already issued, so{' '}
            <Text type="code" color="inherit" className="dj-device-code">
              {phase.pending.userCode}
            </Text>{' '}
            stays pending until it expires on its own. {remaining(phase.pending.expiresInSeconds)}{' '}
            Do not approve it, and do not read it out to anybody who asks for it.
          </Text>
        </Stack>
      </Card>
    );
  }

  // ready | approving — the DECISION, with everything it is made of on screen.
  const pending = phase.pending;
  const busy = phase.kind === 'approving';

  if (pending.status !== 'pending') {
    // An already-approved code reports `approved` (auth.ts), which is how a page
    // reloaded after the hand-off says "done" instead of offering a button that
    // can no longer do anything — a second approval is refused by the server.
    return (
      <Card padding={4} elevation="none">
        <Banner
          status="info"
          container="card"
          title="That code has already been dealt with. Return to your terminal."
        />
      </Card>
    );
  }

  return (
    <Card padding={4} elevation="none">
      <Stack direction="vertical" gap={3} hAlign="start">
        <Text as="p" color="secondary">
          A client calling itself <strong>{pending.clientId}</strong> is asking to sign in as{' '}
          <strong>{actorEmail}</strong>. Check that the code below is the one your terminal printed.
        </Text>

        {/* The code, at reading size. `type="code"` is the design system's
            monospace face; the letter-spacing is this page's own (see header). */}
        <Card variant="muted" padding={3} elevation="none">
          <Text type="code" size="lg" className="dj-device-code">
            {pending.userCode}
          </Text>
        </Card>

        <Text as="p" color="secondary">
          {pending.org === null
            ? 'It named no organization, so approving grants that terminal exactly the memberships you already hold — no more.'
            : `It is asking for the organization ${pending.org}, and can be approved only by a member of it.`}{' '}
          {remaining(pending.expiresInSeconds)}
        </Text>

        {/* role="note" is the contract, and Banner cannot emit it (alert/status
            only) — so the SURFACE is the design system's tinted Card and the role
            is set here. See this file's header. */}
        <Card variant="yellow" padding={3} elevation="none" role="note">
          <Text as="p" type="supporting">
            If you did not just run <code>djc auth login</code> yourself, do not approve this. The
            name above is what the client called itself, not something the forge verified.
          </Text>
        </Card>

        <Stack direction="horizontal" gap={2} wrap="wrap">
          <Button
            type="button"
            variant="primary"
            label="Approve"
            isDisabled={busy}
            onClick={() => onApprove(pending)}
          />
          <Button
            type="button"
            variant="secondary"
            label="Don’t approve"
            isDisabled={busy}
            onClick={() => onDecline(pending)}
          />
        </Stack>
      </Stack>
    </Card>
  );
}

export default DeviceApprovalPage;
